import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Color, LegendPosition, ScaleType } from '@swimlane/ngx-charts';
import { ExamineeService } from 'src/app/services/examinee.service';
import { AssessmentService } from 'src/app/services/assessment.service';
import { ProjectService } from 'src/app/services/project.service';
import { AuthService } from 'src/app/services/auth.service';
import { GenerateNewReportComponent } from '../report-generation/generate-new-report/generate-new-report.component';

@Component({
    selector: 'app-view-assessment-examinee-report',
    templateUrl: './view-assessment-examinee-report.component.html',
    styleUrl: './view-assessment-examinee-report.component.scss',
    standalone: false
})
export class ViewAssessmentExamineeReportComponent implements OnInit, OnDestroy {
  @ViewChild('generateNewReport') generateNewReportModal!: GenerateNewReportComponent;
  
  loading = true;
  isAssessmentMode = false;
  projectId!: number;
  examineeId!: number;
  assessmentId?: number;

  displayData: any = null;

  colorSchemeLine: string | Color = { name: 'myScheme', selectable: true, group: ScaleType.Ordinal, domain: ['#45cb85', '#4b38b3'] };
  colorSchemeTheme: string | Color = { name: 'myScheme', selectable: true, group: ScaleType.Ordinal, domain: ['#4897F0', '#f06548'] };
  theme: string | null = null;
  showXAxis = true;
  showYAxis = true;
  gradient = false;
  showLegend = true; 
  showXAxisLabel = false;
  xAxisLabel = 'Questions';
  showYAxisLabel = false; 
  yAxisLabel = 'Cognitive / Emotion';
  showGridLines = false;
  autoScale = false; 
  legendPosition = LegendPosition.Below;
  activeTab = 1;

  examinee!: any;
  page_data: any = null;
  page_source: string = 'examinee';

  targetReportId: string | null = null;

  constructor(private route: ActivatedRoute, private router: Router, private examineeService: ExamineeService, private assessmentService: AssessmentService, private projectService: ProjectService, private authService: AuthService) { }

  ngOnInit(): void {
    this.theme = document.documentElement.getAttribute('data-bs-theme');
    
    this.route.params.subscribe((params: any) => {
      this.projectId = params.projectid;
      this.examineeId = params.examineid;

      this.page_data = {
				project_id: this.projectId,
				examinee_id: this.examineeId
			}

      this.projectService.setLeftSideBar(true);
      
      if (params.assessmentid) {
        this.isAssessmentMode = true;
        this.assessmentId = params.assessmentid;
        this.page_data.assessment_id = this.assessmentId;
        this.page_source = 'case_examinee';
        this.loadAssessmentData();
      } else {
        this.isAssessmentMode = false;
        this.loadStandardData();
      }
    });
  }

  ngOnDestroy(): void {
    this.projectService.setLeftSideBar(false);    
    setTimeout(() => window.dispatchEvent(new Event('resize')), 50);
  }

  setActiveTab(tabId: number) {
    this.activeTab = tabId;
  }

  loadAssessmentData() {
    this.assessmentService.getProjectCaseActivity(this.projectId, this.assessmentId!, this.examineeId, { activity_type: 'examinee' }).subscribe({
      next: (res: any) => {
        this.examinee = res.activity_data;
        const assessment = res.case_data;
        
        let person;
        let roleSuffix = '';

        // 1. Determine which object holds the person details
        if (this.examinee.associate) {
          person = this.examinee.associate.associate_details;
          roleSuffix = ` (${person.role})`;
        } else {
          person = assessment.subject_details;
        }

        // 2. Apply your Name Logic
        const baseName = person?.first_name ? person?.last_name ? `${person.first_name} ${person.last_name}` : `${person.first_name}` : 'Unknown';

        // 3. Combine
        const fullName = baseName + roleSuffix;

        // Map to Display Data
        this.mapToDisplayData({
          createdAt: this.examinee.createdAt,
          answeredAt: this.examinee.ended_at, 
          fullName: fullName,
          email: null, 
          mobile: null,
          location: null,
          age: !this.examinee.associate ? (assessment.subject_details.age || 0) : null,
          owner: `${assessment.case_owner_user?.person?.first_name} ${assessment.case_owner_user?.person?.last_name}`,
          
          questionnaire: this.examinee.questionnaire,
          finalScore: this.examinee.final_score,
          stressData: this.examinee.stress_chart_data,
          threshData: this.examinee.thresh_chart_data
        });
      },
      error: (err) => this.handleError(err)
    });
  }

  loadStandardData() {
    this.examineeService.getExamineeDetails(this.projectId, this.examineeId).subscribe({
      next: (res: any) => {
        this.examinee = res.examinee;

        const fullName = this.examinee.person?.first_name ? this.examinee.person?.last_name ? `${this.examinee.person?.first_name} ${this.examinee.person?.last_name}` : `${this.examinee.person?.first_name}` : 'Unknown';
        
        this.mapToDisplayData({
          createdAt: this.examinee.createdAt,
          answeredAt: this.examinee.answered_at,
          fullName: fullName,
          email: this.examinee.person?.email,
          mobile: this.examinee.person?.phone ? `+(${this.examinee.person?.phone_country_code}) ${this.examinee.person?.phone}` : null,
          location: (this.examinee.person?.city && this.examinee.person?.country) ? `${this.examinee.person?.city}, ${this.examinee.person?.country}` : null,
          age: null,
          owner: null,
          
          questionnaire: res.questionnaire,
          finalScore: res.final_score,
          stressData: res.stress_chart_data,
          threshData: res.thresh_chart_data
        });
      },
      error: (err) => this.handleError(err)
    });
  }

  // --- Helper: Normalize Data ---
  mapToDisplayData(data: any) {
    this.displayData = {
      ...data,
      totalQuestions: this.calcTotalQuestions(data.questionnaire),
      language: this.getLanguageName(data.questionnaire.lang_code),
      scoreColor: this.getScoreColor(data.finalScore)
    };
    this.loading = false;
  }

  // --- Navigation ---
  goBack() {
    if (this.isAssessmentMode) {
      this.router.navigate(['/projects', this.projectId, 'assessment', this.assessmentId], { queryParams: { tab: 'assessments' } });
    } else {
      this.router.navigate(['/projects/' + this.projectId], { queryParams: { 'tab': 'examinees' } });
    }
  }

  // --- Utilities ---
  handleError(err: any) {
    this.authService.toastErrorMessage('Error!', 'Something went wrong getting report');
    this.goBack();
  }

  calcTotalQuestions(q: any): number {
    return q.chapters.reduce((acc: number, chap: any) => acc + chap.questions.length, 0);
  }

  getLanguageName(code: string) {
    // simplified for brevity
    return code === 'he' ? 'Hebrew' : 'English'; 
  }

  getScoreColor(score: number): string {
    if (score < 25) return '#40bb82';
    if (score < 50) return '#ffca5b';
    if (score < 85) return '#f1963b';
    return '#ee6352';
  }

  openGenerateNewReportModal() {
		this.generateNewReportModal.openModal(this.examinee.extra_content?.analyze_version || 0);
	}

  handleUpdateReports(event: any) {
		this.examinee = { ...this.examinee, ...event.entity };
		if (this.examinee.status === 'ANALYZING' || this.examinee.status === 'STARTED') {
			this.goBack();
		} else {
      if (event.reportId) {
        this.targetReportId = event.reportId;
      }
    }
	}
}
